import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useProductInitWorkflowLatest,
  useRetryProductInitialization,
} from '@/shared/hooks/api/product'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  type ProductInitStepKey,
  getProductInitLoaderState,
} from '@/features/product-catalog/utils/productInitWorkflowDisplay'

export function ProductInitializationView({
  productId,
  isIngesting,
}: {
  productId: string
  isIngesting: boolean
}) {
  const { t } = useTranslation('productCatalog')
  const { data: workflow } = useProductInitWorkflowLatest(productId)
  const retryInitialization = useRetryProductInitialization(productId)

  const { headlineKey, latestStepKey } = useMemo(
    () =>
      getProductInitLoaderState({
        workflow,
        isIngesting,
      }),
    [workflow, isIngesting]
  )

  const headline = headlineText(headlineKey, t)
  const stepLine =
    latestStepKey != null
      ? t(
          `initWorkflow.steps.${latestStepKey}`,
          stepDefaultLabel(latestStepKey)
        )
      : t('initWorkflow.latestStepFallback')

  const [retryRequested, setRetryRequested] = useState(false)

  useEffect(() => {
    if (workflow?.status !== 'failed') {
      setRetryRequested(false)
    }
  }, [workflow?.status])

  const workflowFailed =
    workflow?.status === 'failed' && typeof workflow.error === 'string'
  const isRetrying = retryInitialization.isPending || retryRequested
  const showLoading = !workflowFailed || isRetrying
  const showFailedState = workflowFailed && !isRetrying

  async function handleRetryClick() {
    setRetryRequested(true)
    try {
      await retryInitialization.mutateAsync()
    } catch {
      setRetryRequested(false)
    }
  }

  return (
    <Card className='flex h-full min-h-[280px] flex-col'>
      <CardContent className='flex flex-1 flex-col items-center justify-center gap-4 py-10'>
        {showLoading && (
          <Loader2
            className='size-10 animate-spin text-muted-foreground'
            aria-hidden
          />
        )}

        {showFailedState && (
          <AlertCircle
            className='size-10 text-destructive'
            aria-hidden
          />
        )}

        {showLoading &&
          (headlineKey === 'waitingBatch' || headlineKey === 'ingestion') && (
            <p className='max-w-sm text-center text-sm font-medium'>
              <InitShimmerText>{headline}</InitShimmerText>
            </p>
          )}

        {showLoading && headlineKey === 'workflow' && (
          <div className='flex max-w-sm flex-col gap-1 text-center'>
            <p className='text-sm font-medium'>
              <InitShimmerText>{stepLine}</InitShimmerText>
            </p>
            <p className='text-xs text-muted-foreground'>
              <InitShimmerText className='text-xs'>{headline}</InitShimmerText>
            </p>
          </div>
        )}

        {showFailedState && (
          <div className='flex max-w-sm flex-col gap-3 text-center'>
            <div className='flex flex-col gap-1'>
              <p className='text-sm font-medium text-foreground'>{stepLine}</p>
              <p className='text-xs text-muted-foreground'>{headline}</p>
            </div>
            <p className='text-xs text-destructive' role='alert'>
              {workflow.error}
            </p>
            <Button
              type='button'
              variant='secondary'
              onClick={handleRetryClick}
              disabled={isRetrying}
            >
              {t('initWorkflow.tryAgain', 'Try again')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function InitShimmerText({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-block bg-size-[200%_100%] bg-clip-text text-transparent',
        'bg-linear-to-r from-muted-foreground via-foreground to-muted-foreground',
        'animate-[init-text-shimmer_2.5s_linear_infinite] bg-position-[0%_50%]',
        className
      )}
    >
      {children}
    </span>
  )
}

function headlineText(
  headlineKey: ReturnType<typeof getProductInitLoaderState>['headlineKey'],
  translate: (key: string, defaultValue: string) => string
): string {
  if (headlineKey === 'waitingBatch') {
    return translate(
      'initWorkflow.headlineWaitingBatch',
      'Waiting for all uploads to finish…'
    )
  }
  if (headlineKey === 'ingestion') {
    return translate('initWorkflow.headlineIngestion', 'Processing documents…')
  }
  return translate(
    'initWorkflow.headlineWorkflow',
    'Setting up product details…'
  )
}

function stepDefaultLabel(key: ProductInitStepKey): string {
  switch (key) {
    case 'ingestion':
      return 'Ingestion and indexing'
    case 'classify_document':
      return 'Classifying sources'
    case 'extract_product':
      return 'Extracting product details'
    case 'persist_result':
      return 'Saving product details'
    default:
      return key
  }
}
